import React, { useEffect, useState } from "react";
import { CreditCard, Save, Eye, EyeOff, IndianRupee, CheckCircle2, Clock, AlertCircle, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";

const STATUS_PILL = {
  captured: { cls: "bg-emerald-50 text-emerald-700", icon: CheckCircle2, label: "Captured" },
  created: { cls: "bg-amber-50 text-amber-700", icon: Clock, label: "Pending" },
  failed: { cls: "bg-rose-50 text-rose-700", icon: AlertCircle, label: "Failed" },
};

export default function PaymentSettings() {
  // Razorpay keys
  const [keys, setKeys] = useState({
    razorpay_mode: "test",
    razorpay_key_id: "",
    razorpay_key_secret: "",
    razorpay_webhook_secret_set: false,
  });
  const [secretInput, setSecretInput] = useState("");
  const [webhookInput, setWebhookInput] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);

  // Customer payments
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ total_captured: 0, captured_count: 0, items: [] });

  const loadKeys = async () => {
    const r = await api.get("/payments/tenant-razorpay");
    setKeys(r.data);
  };
  const loadPayments = async (status = filter) => {
    const r = await api.get("/admin/customer-payments", { params: { status } });
    setData(r.data);
  };
  useEffect(() => { loadKeys(); loadPayments("all"); }, []);
  useEffect(() => { loadPayments(filter); /* eslint-disable-line */ }, [filter]);

  const saveKeys = async () => {
    setSaving(true);
    try {
      const payload = {
        razorpay_mode: keys.razorpay_mode,
        razorpay_key_id: keys.razorpay_key_id,
      };
      if (secretInput && !secretInput.startsWith("*")) payload.razorpay_key_secret = secretInput;
      if (webhookInput && !webhookInput.startsWith("*")) payload.razorpay_webhook_secret = webhookInput;
      const r = await api.patch("/payments/tenant-razorpay", payload);
      setKeys(r.data);
      setSecretInput("");
      setWebhookInput("");
      toast.success("Razorpay settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const filtered = data.items.filter((it) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (it.customer_name || "").toLowerCase().includes(q) ||
           (it.customer_phone || "").includes(q) ||
           (it.order_id || "").toLowerCase().includes(q);
  });

  const hasLiveKeys = !!(keys.razorpay_key_id && keys.razorpay_key_secret);

  return (
    <div className="space-y-5" data-testid="admin-payment-settings-page">
      {/* Razorpay keys card */}
      <div className="bg-white border border-slate-100 rounded-3xl p-5 card-shadow">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-extrabold tracking-tight text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-tenant-primary" /> Razorpay (Receive from Customers)
            </h2>
            <p className="text-xs text-slate-500 mt-1">Configure your Razorpay account so your customers can top-up their Saving Wallet directly into your bank account.</p>
          </div>
          <span
            data-testid="rzp-status-badge"
            className={`text-[10px] font-extrabold px-2.5 py-1 rounded-full uppercase tracking-wider shrink-0 ${
              hasLiveKeys ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
            }`}
          >
            {hasLiveKeys ? "Live" : "Not configured"}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Mode</label>
            <select
              data-testid="rzp-mode"
              value={keys.razorpay_mode || "test"}
              onChange={(e) => setKeys({ ...keys, razorpay_mode: e.target.value })}
              className="mt-1.5 w-full border-2 border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-tenant-primary"
            >
              <option value="test">Test Mode</option>
              <option value="live">Live Mode</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Key ID</label>
            <input
              data-testid="rzp-key-id"
              value={keys.razorpay_key_id || ""}
              onChange={(e) => setKeys({ ...keys, razorpay_key_id: e.target.value })}
              placeholder="rzp_test_xxxxxxxxxxxx"
              className="mt-1.5 w-full border-2 border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-tenant-primary font-mono text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Key Secret</label>
            <div className="mt-1.5 relative">
              <input
                data-testid="rzp-key-secret"
                type={showSecret ? "text" : "password"}
                value={secretInput || (keys.razorpay_key_secret || "")}
                onChange={(e) => setSecretInput(e.target.value)}
                placeholder={keys.razorpay_key_secret ? "Leave blank to keep existing" : "Enter Razorpay Key Secret"}
                className="w-full border-2 border-slate-200 rounded-2xl px-4 py-3 pr-12 outline-none focus:border-tenant-primary font-mono text-sm"
              />
              <button onClick={() => setShowSecret((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" type="button" data-testid="toggle-secret">
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Webhook Secret (Optional)</label>
            <input
              data-testid="rzp-webhook-secret"
              type="password"
              value={webhookInput}
              onChange={(e) => setWebhookInput(e.target.value)}
              placeholder={keys.razorpay_webhook_secret_set ? "Already set — leave blank to keep" : "For webhook signature verification"}
              className="mt-1.5 w-full border-2 border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-tenant-primary font-mono text-sm"
            />
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button
            data-testid="rzp-save"
            onClick={saveKeys}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-tenant-primary text-white px-5 py-3 rounded-2xl font-bold tap-scale disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Settings"}
          </button>
          <a
            href="https://dashboard.razorpay.com/app/keys"
            target="_blank"
            rel="noreferrer"
            className="text-xs font-bold text-tenant-primary underline"
            data-testid="rzp-keys-help"
          >
            Where to find my keys?
          </a>
        </div>
        {!hasLiveKeys && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-[12px] text-amber-800" data-testid="rzp-not-configured">
            Until live keys are added, customer add-money flows operate in MOCK mode (instant success, no real money moves).
          </div>
        )}
      </div>

      {/* Customer payments list */}
      <div className="bg-white border border-slate-100 rounded-3xl p-5 card-shadow">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-extrabold tracking-tight text-lg flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-emerald-600" /> Customer Payments
            </h2>
            <p className="text-xs text-slate-500 mt-1">All Razorpay top-ups received from your customers.</p>
          </div>
          <button
            onClick={() => loadPayments(filter)}
            data-testid="rzp-payments-refresh"
            className="text-xs font-bold text-slate-500 inline-flex items-center gap-1 hover:text-tenant-primary"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-3">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-emerald-700">Total Captured</div>
            <div data-testid="rzp-total-captured" className="text-2xl font-extrabold tracking-tight mt-1 text-emerald-700">{fmtINR(data.total_captured || 0)}</div>
          </div>
          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Successful Payments</div>
            <div data-testid="rzp-captured-count" className="text-2xl font-extrabold tracking-tight mt-1">{data.captured_count || 0}</div>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {["all", "captured", "created"].map((s) => (
            <button
              key={s}
              data-testid={`rzp-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider ${
                filter === s ? "bg-tenant-primary text-white" : "bg-slate-100 text-slate-600"
              }`}
            >
              {s === "created" ? "Pending" : s}
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="rzp-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name / phone / order id"
              className="border border-slate-200 rounded-full pl-8 pr-3 py-1.5 text-xs outline-none focus:border-tenant-primary w-64 max-w-full"
            />
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="rzp-payments-table">
            <thead className="text-[10px] uppercase tracking-wider text-slate-400">
              <tr className="border-b border-slate-100">
                <th className="text-left py-2 pr-2">Customer</th>
                <th className="text-left py-2 px-2">Amount</th>
                <th className="text-left py-2 px-2">Status</th>
                <th className="text-left py-2 px-2 hidden md:table-cell">Order ID</th>
                <th className="text-left py-2 px-2 hidden md:table-cell">Payment ID</th>
                <th className="text-left py-2 pl-2">Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => {
                const pill = STATUS_PILL[it.status] || STATUS_PILL.created;
                const PillIcon = pill.icon;
                return (
                  <tr key={it.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-3 pr-2">
                      <div className="font-bold text-sm">{it.customer_name}</div>
                      <div className="text-[11px] text-slate-400">{it.customer_phone}</div>
                    </td>
                    <td className="py-3 px-2 font-extrabold">{fmtINR(it.amount)}</td>
                    <td className="py-3 px-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-1 rounded-full uppercase tracking-wider ${pill.cls}`}>
                        <PillIcon className="w-3 h-3" /> {pill.label}
                      </span>
                      {it.mocked && <span className="ml-1 text-[10px] font-bold text-amber-600">MOCK</span>}
                    </td>
                    <td className="py-3 px-2 font-mono text-[11px] text-slate-500 hidden md:table-cell">{it.order_id}</td>
                    <td className="py-3 px-2 font-mono text-[11px] text-slate-500 hidden md:table-cell">{it.payment_id || "—"}</td>
                    <td className="py-3 pl-2 text-[11px] text-slate-400">{fmtDate(it.created_at)}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-slate-400 text-sm" data-testid="rzp-payments-empty">No customer payments yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
